import { Bvh } from './Bvh';

/**
 * Per-template BVH cache, keyed by content hash.
 *
 * Mirrors the role of `MeshVoxelStampStore` for the volgrid path: each unique
 * placed-object template (e.g. one tree variant, one rock variant) gets its
 * BLAS (Bottom-Level Acceleration Structure) built once at scene-load time
 * and reused across every instance that references it. The numeric content
 * hash matches the same one used by `MeshVoxelStampStore`, so the manifest's
 * `stampsByTemplate` map doubles as the template → BLAS key resolver.
 *
 * Build path: this store is passive — it just holds and serves. Callers
 * (typically the tile streamer during `loadTile`) build the BLAS from
 * `Scene.modelRegistry` when they discover a missing template, then `add()`
 * it here. Future enhancement: bake BLAS into GLB extras alongside the LOD
 * kit / voxel stamp and `setAll()` from a parsed payload.
 */
export class BlasStore {
  private blases = new Map<number, Bvh>();

  /** Number of unique BLAS entries. */
  get size(): number {
    return this.blases.size;
  }

  /** Sum of triangle counts across all BLAS entries — useful for diagnostics. */
  get totalTriangles(): number {
    let sum = 0;
    for (const bvh of this.blases.values()) sum += bvh.indices.length / 3;
    return sum;
  }

  get(contentHash: number): Bvh | undefined {
    return this.blases.get(contentHash);
  }

  has(contentHash: number): boolean {
    return this.blases.has(contentHash);
  }

  add(contentHash: number, bvh: Bvh): void {
    this.blases.set(contentHash, bvh);
  }

  /** Replace contents from a parsed list (used after fetch+decompress). */
  setAll(entries: ReadonlyArray<{ contentHash: number; bvh: Bvh }>): void {
    this.blases.clear();
    for (const e of entries) this.blases.set(e.contentHash, e.bvh);
  }

  clear(): void {
    this.blases.clear();
  }
}
